import { createContext, useContext, useEffect, useState } from "react";
import { useMember } from "./memberContext";
import type { BodyFont } from "@/types/MemberSettings";

const FONT_FAMILIES: Record<BodyFont, string> = {
  Outfit: '"Outfit", "Roboto", sans-serif',
  Roboto: '"Roboto", "Outfit", sans-serif',
  "EB Garamond": '"EB Garamond", serif',
};

interface FontContextType {
  bodyFont: BodyFont;
  setBodyFont: (font: BodyFont) => void;
}

const FontContext = createContext<FontContextType | undefined>(undefined);

export function FontProvider({ children }: { children: React.ReactNode }) {
  const { member } = useMember();
  const [bodyFont, setBodyFontState] = useState<BodyFont>("EB Garamond");

  // Initialize font from member settings or localStorage
  useEffect(() => {
    const initializeFont = () => {
      // Priority: member settings > localStorage > default EB Garamond
      const memberFont = member?.settings?.bodyFont as BodyFont | undefined;
      const storedFont = localStorage.getItem("bodyFont") as BodyFont | null;
      const initialFont = memberFont || storedFont || "EB Garamond";

      setBodyFontState(initialFont);
      applyFont(initialFont);
    };

    initializeFont();
  }, [member?.settings?.bodyFont]);

  // Apply font to document body
  const applyFont = (newFont: BodyFont) => {
    const fontFamily = FONT_FAMILIES[newFont];
    document.body.style.fontFamily = fontFamily;

    // Also update table headers which use the same font
    document.documentElement.style.setProperty("--body-font-family", fontFamily);

    // Store in localStorage for persistence across sessions
    localStorage.setItem("bodyFont", newFont);
  };

  const setBodyFont = (newFont: BodyFont) => {
    setBodyFontState(newFont);
    applyFont(newFont);
  };

  return (
    <FontContext.Provider value={{ bodyFont, setBodyFont }}>
      {children}
    </FontContext.Provider>
  );
}

export function useFont() {
  const context = useContext(FontContext);
  if (!context) {
    throw new Error("useFont must be used within a FontProvider");
  }
  return context;
}
